import { linear } from './math';
import { drawArc, drawArea, drawCircle, drawPath, drawRect, straight } from './shapes';

export function rgb(color) {
  return `rgb(${Math.round(color.r)} ${Math.round(color.g)} ${Math.round(color.b)})`;
}

function isPlain(value) {
  return value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;
}

function copy(value) {
  if (Array.isArray(value)) return [...value];
  if (isPlain(value)) return { ...value };

  return value;
}

export class Entity {
  #r = NaN;
  #g = NaN;
  #b = NaN;
  #style = '';

  constructor({ draw, color, x0 = 0, x1 = 0, y0 = 0, y1 = 0, alpha = 1, lineWidth = 1, ...props } = {}) {
    Object.assign(this, { draw, x0, x1, y0, y1, alpha, lineWidth }, props);
    this.color = { r: 0, g: 0, b: 0, ...color };
  }

  get style() {
    const { r, g, b } = this.color;

    if (r !== this.#r || g !== this.#g || b !== this.#b) {
      this.#r = r;
      this.#g = g;
      this.#b = b;
      this.#style = rgb(this.color);
    }

    return this.#style;
  }

  snapshot() {
    return Object.fromEntries(Object.entries(this).map(([key, value]) => [key, copy(value)]));
  }

  restore(state) {
    for (const [key, from] of Object.entries(state)) {
      const into = this[key];

      if (Array.isArray(from) && Array.isArray(into)) into.splice(0, into.length, ...from);
      else if (isPlain(from) && isPlain(into)) Object.assign(into, from);
      else this[key] = copy(from);
    }
  }
}

export function rect(options) {
  return new Entity({ ...options, draw: drawRect });
}

export function path(options) {
  return new Entity({ ease: linear, offset: straight, t0: 0, t1: 1, segments: 32, ...options, draw: drawPath });
}

export function area(options) {
  return new Entity({ ease: linear, offset: straight, t0: 0, t1: 1, segments: 32, base: 0, ...options, draw: drawArea });
}

export function circle(options) {
  return new Entity({ ...options, draw: drawCircle });
}

export function arc(options) {
  return new Entity({ startAngle: 0, endAngle: Math.PI * 2, ...options, draw: drawArc });
}
